import { useState } from "react";
import { X, ChevronLeft } from "lucide-react";
import FieldOfWork from "components/Reusable/FieldOfWork";
import { BASE_URL } from "utils/constant";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
  const variants = {
    default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
    destructive:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline:
      "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-sm-accent-foreground",
    secondary:
      "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
    ghost: "hover:bg-accent hover:text-sm-accent-foreground",
    link: "text-sm-primary underline-offset-4 hover:underline",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  };
  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Label = ({ children, className = "" }) => (
  <label
    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
  >
    {children}
  </label>
);

export function AddContractorDialog({ open, onOpenChange, onAddContractor }) {
  const [newContractor, setNewContractor] = useState({
    name: "",
    category: "",
    workname_id: null,
    other_workname: null,
    address: "",
    email: "",
  });
  // Separate state for the raw 10-digit number (no prefix)
  const [contactNumber, setContactNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [showFieldOfWork, setShowFieldOfWork] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Strips non-digits, caps at 10, validates length in real time
  const handleNumberInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const handleAddContractor = async () => {
    // 10-digit guard before anything else
    if (contactNumber.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }
    setPhoneError("");

    if (newContractor.name && (newContractor.workname_id || newContractor.other_workname)) {
      setIsSubmitting(true);
      setApiError(null);
      try {
        const token = localStorage.getItem("access");
        const organization =
          JSON.parse(localStorage.getItem("organization")) || {};

        const payload = {
          organization: organization.id,
          name: newContractor.name,
          workname_id: newContractor.workname_id,
          other_workname: newContractor.other_workname,
          contact: "+91" + contactNumber, // attach prefix only for the API
          address: newContractor.address,
        };

        const response = await fetch(`${BASE_URL}/org/contractors/create/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (data.status) {
          onAddContractor({
            ...newContractor,
            contact: "+91" + contactNumber,
            id: data.data.id,
          });
          // Reset form
          setNewContractor({
            name: "",
            category: "",
            workname_id: null,
            other_workname: null,
            address: "",
            email: "",
          });
          setContactNumber("");
          onOpenChange(false);
        } else {
          setApiError(data.message || "Failed to create contractor");
        }
      } catch (err) {
        console.error("Error creating contractor:", err);
        setApiError("An error occurred while creating contractor");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setApiError(
        "Please fill in all required fields (Name, Category, Contact)",
      );
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      {!showFieldOfWork ? (
        <div className="relative w-full max-w-md rounded-lg border bg-background p-6 shadow-lg animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
            <h3 className="text-lg font-semibold leading-none tracking-tight">
              Add New Contractor
            </h3>
          </div>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm">Contractor Name *</Label>
              <Input
                placeholder="Enter contractor name"
                value={newContractor.name}
                onChange={(e) =>
                  setNewContractor({ ...newContractor, name: e.target.value })
                }
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Category *</Label>
              <button
                type="button"
                onClick={() => setShowFieldOfWork(true)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-left hover:bg-accent"
              >
                {newContractor.category || "Select category"}
              </button>
            </div>

            {/* ── Contact number with +91 prefix & 10-digit validation ── */}
            <div className="space-y-2">
              <Label className="text-sm">Contact Number *</Label>
              <div
                className={`flex items-center h-10 rounded-md border bg-transparent px-3 text-sm shadow-sm transition-colors ${phoneError ? "border-red-500" : "border-input"
                  }`}
              >
                <span className="mr-2 text-muted-foreground select-none">
                  +91
                </span>
                <input
                  type="text"
                  maxLength={10}
                  value={contactNumber}
                  onInput={handleNumberInput}
                  placeholder="9999999999"
                  required
                  className="w-full bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
              {phoneError && (
                <p className="text-xs text-red-500 font-medium">{phoneError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={newContractor.email}
                onChange={(e) =>
                  setNewContractor({ ...newContractor, email: e.target.value })
                }
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Address</Label>
              <Input
                placeholder="Enter address"
                value={newContractor.address}
                onChange={(e) =>
                  setNewContractor({
                    ...newContractor,
                    address: e.target.value,
                  })
                }
                className="h-10"
              />
            </div>
            {apiError && (
              <p className="text-xs text-destructive font-medium">{apiError}</p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddContractor}
                className="bg-sm-primary hover:bg-sm-primary/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Contractor"}
              </Button>
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      ) : (
        <div className="relative w-full max-w-md rounded-lg border bg-background p-6 shadow-lg animate-in fade-in-0 zoom-in-95 duration-200">
          <button
            type="button"
            onClick={() => setShowFieldOfWork(false)}
            className="flex items-center mb-2 text-sm-primary text-sm font-medium hover:underline gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to form
          </button>
          <FieldOfWork
            onSelect={(work) => {
              console.log(work);
              setNewContractor({
                ...newContractor,
                category: work.name,
                workname_id: work.id,
                other_workname: work.other_workname,
              });
              setShowFieldOfWork(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
